import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { CheckCircle2, Clock, AlertCircle, Calendar, Users, FileText, MapPin } from "lucide-react";

const timelineEvents = [
  {
    id: 1,
    title: "Project Initiation",
    description: "Park master plan project officially launched with community stakeholders",
    date: "2024-01-15",
    status: "completed",
    type: "milestone",
    participants: ["Local Council", "Planning Team", "Community Representatives"]
  },
  {
    id: 2,
    title: "Site Analysis & Research",
    description: "Comprehensive analysis of existing park facilities, usage patterns, and environmental factors",
    date: "2024-02-20",
    status: "completed",
    type: "research",
    participants: ["Planning Team", "Environmental Consultants", "Engineers"]
  },
  {
    id: 3,
    title: "Community Visioning Workshop #1",
    description: "First public workshop to understand community needs and vision for the park",
    date: "2024-03-30",
    status: "completed",
    type: "engagement",
    participants: ["Community Members", "Planning Team", "Facilitators"]
  },
  {
    id: 4,
    title: "Demographic & Usage Study",
    description: "Analysis of local demographics and current park usage patterns",
    date: "2024-04-15",
    status: "completed",
    type: "research",
    participants: ["Data Analysts", "Community Surveyors"]
  },
  {
    id: 5,
    title: "Community Survey Launch",
    description: "Comprehensive online and paper survey launched to gather broader community input",
    date: "2024-05-01",
    status: "completed",
    type: "engagement",
    participants: ["All Local Residents", "Planning Team"]
  },
  {
    id: 6,
    title: "Public Information Sessions",
    description: "Series of public meetings to present initial findings and gather feedback",
    date: "2024-06-15",
    status: "completed",
    type: "engagement",
    participants: ["Community Members", "Planning Team", "Council Representatives"]
  },
  {
    id: 7,
    title: "Children & Family Focus Groups",
    description: "Targeted sessions with families to understand playground and family facility needs",
    date: "2024-07-20",
    status: "completed",
    type: "engagement",
    participants: ["Parents", "Children", "Youth Groups", "Planning Team"]
  },
  {
    id: 8,
    title: "Draft Design Development",
    description: "Creating initial design concepts based on community feedback and technical requirements",
    date: "2024-08-15",
    status: "in-progress",
    type: "design",
    participants: ["Landscape Architects", "Engineers", "Planning Team"]
  },
  {
    id: 9,
    title: "Community Design Workshop",
    description: "Interactive workshop to review and refine draft designs with community input",
    date: "2024-09-15",
    status: "upcoming",
    type: "engagement",
    participants: ["Community Members", "Design Team", "Facilitators"]
  },
  {
    id: 10,
    title: "Final Public Consultation",
    description: "Present final draft plans and gather community feedback before council submission",
    date: "2024-10-30",
    status: "upcoming",
    type: "engagement",
    participants: ["General Public", "Stakeholders", "Planning Team"]
  },
  {
    id: 11,
    title: "Council Submission Preparation",
    description: "Finalize master plan documents and prepare formal submission to council",
    date: "2024-11-30",
    status: "upcoming",
    type: "deliverable",
    participants: ["Planning Team", "Legal Team", "Council Liaison"]
  },
  {
    id: 12,
    title: "Council Presentation",
    description: "Formal presentation of park master plan to local council for approval",
    date: "2024-12-20",
    status: "upcoming",
    type: "milestone",
    participants: ["Council Members", "Planning Team", "Community Representatives"]
  }
];

const upcomingMilestones = [
  {
    title: "Community Design Workshop",
    date: "2024-09-15",
    daysUntil: 16,
    description: "Review and refine draft designs",
    priority: "high"
  },
  {
    title: "Survey Period Ends",
    date: "2024-09-30",
    daysUntil: 31,
    description: "Last chance for survey responses",
    priority: "medium"
  },
  {
    title: "Final Public Consultation",
    date: "2024-10-30",
    daysUntil: 61,
    description: "Present final plans to community",
    priority: "high"
  }
];

const getStatusIcon = (status) => {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case "in-progress":
      return <Clock className="h-4 w-4 text-blue-600" />;
    case "upcoming":
      return <AlertCircle className="h-4 w-4 text-yellow-600" />;
    default:
      return <Clock className="h-4 w-4 text-gray-600" />;
  }
};

const getStatusBadgeColor = (status) => {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-800 hover:bg-green-100";
    case "in-progress":
      return "bg-blue-100 text-blue-800 hover:bg-blue-100";
    case "upcoming":
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100";
  }
};

const getTypeIcon = (type) => {
  switch (type) {
    case "milestone":
      return <CheckCircle2 className="h-3 w-3" />;
    case "engagement":
      return <Users className="h-3 w-3" />;
    case "research":
    case "deliverable":
      return <FileText className="h-3 w-3" />;
    case "design":
      return <MapPin className="h-3 w-3" />;
    default:
      return <Calendar className="h-3 w-3" />;
  }
};

export function TimelineTab() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1>Project Timeline</h1>
          <p className="text-muted-foreground mt-1">
            Key milestones, community engagement activities, and project deliverables
          </p>
        </div>
        <Badge variant="secondary" className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          Updated Daily
        </Badge>
      </div>

      {/* Upcoming Milestones */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Events</CardTitle>
          <CardDescription>Important dates and opportunities for community participation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {upcomingMilestones.map((milestone, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <Badge 
                    variant="secondary"
                    className={milestone.priority === "high" ? "bg-red-100 text-red-800 hover:bg-red-100" : "bg-yellow-100 text-yellow-800 hover:bg-yellow-100"}
                  >
                    {milestone.priority} priority
                  </Badge>
                  <span className="text-sm font-medium">{milestone.daysUntil} days</span>
                </div>
                <h3 className="font-medium">{milestone.title}</h3>
                <p className="text-sm text-muted-foreground">{milestone.description}</p>
                <div className="text-sm text-muted-foreground">
                  Date: {new Date(milestone.date).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Project Phases */}
      <Card>
        <CardHeader>
          <CardTitle>Project Phases Overview</CardTitle>
          <CardDescription>The four main phases of park master plan development</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <h4 className="font-medium">Planning & Research</h4>
              </div>
              <p className="text-sm text-green-700">Jan - Apr 2024</p>
              <p className="text-xs text-green-600 mt-1">Completed ✓</p>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <h4 className="font-medium">Community Consultation</h4>
              </div>
              <p className="text-sm text-blue-700">May - Sep 2024</p>
              <p className="text-xs text-blue-600 mt-1">In Progress (75%)</p>
            </div>
            
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-5 w-5 text-yellow-600" />
                <h4 className="font-medium">Design Development</h4>
              </div>
              <p className="text-sm text-yellow-700">Aug - Nov 2024</p>
              <p className="text-xs text-yellow-600 mt-1">In Progress (45%)</p>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-5 w-5 text-gray-600" />
                <h4 className="font-medium">Council Proposal</h4>
              </div>
              <p className="text-sm text-gray-700">Dec 2024 - Mar 2025</p>
              <p className="text-xs text-gray-600 mt-1">Upcoming</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Project Timeline</CardTitle>
          <CardDescription>Chronological view of all project activities and community engagement opportunities</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {timelineEvents.map((event, index) => (
              <div key={event.id} className="relative">
                {/* Timeline line */}
                {index !== timelineEvents.length - 1 && (
                  <div className="absolute left-4 top-8 w-0.5 h-16 bg-border"></div>
                )}
                
                <div className="flex gap-4">
                  {/* Timeline dot */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-full border-2 bg-background flex items-center justify-center">
                    {getStatusIcon(event.status)}
                  </div>
                  
                  {/* Event content */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium">{event.title}</h3>
                      <Badge 
                        variant="secondary" 
                        className={getStatusBadgeColor(event.status)}
                      >
                        {event.status.replace("-", " ")}
                      </Badge>
                      <Badge 
                        variant="outline" 
                        className="flex items-center gap-1"
                      >
                        {getTypeIcon(event.type)}
                        {event.type}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-muted-foreground">{event.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        {new Date(event.date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {event.participants.join(", ")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* How to Get Involved */}
      <Card>
        <CardHeader>
          <CardTitle>How to Get Involved</CardTitle>
          <CardDescription>Opportunities for community participation in the planning process</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg text-center space-y-2">
              <FileText className="h-8 w-8 mx-auto text-blue-600" />
              <h4 className="font-medium">Complete Our Survey</h4>
              <p className="text-sm text-muted-foreground">
                Share your thoughts on park improvements and priorities
              </p>
              <Button variant="outline" size="sm">Take Survey</Button>
            </div>
            
            <div className="p-4 border rounded-lg text-center space-y-2">
              <Users className="h-8 w-8 mx-auto text-green-600" />
              <h4 className="font-medium">Attend Public Meetings</h4>
              <p className="text-sm text-muted-foreground">
                Join community workshops and information sessions
              </p>
              <Button variant="outline" size="sm">View Schedule</Button>
            </div>
            
            <div className="p-4 border rounded-lg text-center space-y-2">
              <Calendar className="h-8 w-8 mx-auto text-purple-600" />
              <h4 className="font-medium">Stay Updated</h4>
              <p className="text-sm text-muted-foreground">
                Receive notifications about upcoming events and milestones
              </p>
              <Button variant="outline" size="sm">Subscribe</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}